/**
 * Terminal utility functions.
 *
 * @module components/terminal/utils
 */

import type { Terminal } from '@xterm/xterm';

/**
 * Options for creating a terminal keyboard handler.
 */
export interface TerminalKeyHandlerOptions {
  /** The xterm.js Terminal instance */
  terminal: Terminal;
  /** Callback to send input to the terminal (for paste). If not provided, paste is copy-only. */
  sendInput?: (text: string) => void;
  /** Callback to check if the terminal is connected. Required for interactive terminals. */
  isConnected?: () => boolean;
  /** Whether this is a read-only terminal (disables paste and Ctrl shortcuts) */
  readOnly?: boolean;
}

/**
 * Creates a keyboard event handler for xterm.js terminals.
 * Handles copy/paste shortcuts (Shift+Ctrl+C/V on Windows/Linux, Cmd+C/V on Mac)
 * and optionally captures Ctrl shortcuts for interactive terminals.
 *
 * @param options - Configuration options
 * @returns A function suitable for terminal.attachCustomKeyEventHandler()
 */
export function createTerminalKeyHandler(options: TerminalKeyHandlerOptions) {
  const { terminal, sendInput, isConnected, readOnly = false } = options;

  return (event: KeyboardEvent): boolean => {
    // Handle Shift+Ctrl+C (copy) - do it ourselves to avoid DevTools opening
    if (
      event.ctrlKey &&
      event.shiftKey &&
      event.key.toLowerCase() === 'c' &&
      event.type === 'keydown'
    ) {
      const selection = terminal.getSelection();
      if (selection) {
        navigator.clipboard.writeText(selection).catch(() => {});
      }
      event.preventDefault();
      return false;
    }

    // Handle Shift+Ctrl+V (paste) - do it ourselves
    if (
      event.ctrlKey &&
      event.shiftKey &&
      event.key.toLowerCase() === 'v' &&
      event.type === 'keydown'
    ) {
      if (!readOnly && sendInput) {
        navigator.clipboard
          .readText()
          .then((text) => {
            if (text && (isConnected?.() ?? true)) {
              sendInput(text);
            }
          })
          .catch(() => {});
      }
      event.preventDefault();
      return false;
    }

    // Also support Cmd+C/V on Mac for copy/paste
    if (event.metaKey && !event.ctrlKey && event.type === 'keydown') {
      const key = event.key.toLowerCase();
      if (key === 'c') {
        const selection = terminal.getSelection();
        if (selection) {
          navigator.clipboard.writeText(selection).catch(() => {});
        }
        event.preventDefault();
        return false;
      }
      if (key === 'v') {
        if (!readOnly && sendInput) {
          navigator.clipboard
            .readText()
            .then((text) => {
              if (text && (isConnected?.() ?? true)) {
                sendInput(text);
              }
            })
            .catch((err) => {
              console.error('[Terminal] Clipboard read failed:', err);
            });
        }
        event.preventDefault();
        return false;
      }
    }

    // For read-only terminals, don't capture any other shortcuts
    if (readOnly) {
      return true;
    }

    // Only intercept other shortcuts when terminal is focused and connected
    if (!isConnected?.()) {
      return true;
    }

    // List of Ctrl+ shortcuts to capture for the terminal instead of browser
    const ctrlShortcuts = [
      'w', // Close tab
      't', // New tab
      'n', // New window
      'k', // Clear terminal
      'l', // Clear screen
      'u', // Clear line
      'a', // Start of line
      'e', // End of line
      'd', // EOF / exit
      'c', // Interrupt (only without Shift)
      'z', // Suspend
    ];

    if (event.ctrlKey && !event.shiftKey && !event.altKey && !event.metaKey) {
      const key = event.key.toLowerCase();
      if (ctrlShortcuts.includes(key)) {
        event.preventDefault();
        event.stopPropagation();
        return true;
      }
    }

    return true;
  };
}

/**
 * Extracts all text content from an xterm.js terminal buffer.
 *
 * @param terminal - The xterm.js Terminal instance
 * @returns The terminal buffer content as a string, or empty string if terminal is null
 */
export function getTerminalContent(terminal: Terminal | null): string {
  if (!terminal) return '';

  const buffer = terminal.buffer.active;
  let text = '';
  for (let i = 0; i < buffer.length; i++) {
    const line = buffer.getLine(i)?.translateToString(true);
    if (line) {
      text += line + '\n';
    }
  }
  return text.trim();
}

/**
 * Copies terminal content to clipboard.
 *
 * @param terminal - The xterm.js Terminal instance
 * @returns Promise that resolves to true if copy succeeded, false otherwise
 */
export async function copyTerminalContent(terminal: Terminal | null): Promise<boolean> {
  const content = getTerminalContent(terminal);
  if (!content) return false;

  try {
    await navigator.clipboard.writeText(content);
    return true;
  } catch (err) {
    console.error('[Terminal] Failed to copy:', err);
    return false;
  }
}
